/**
 * features/pos/store/heldStore.ts
 * Store de tickets en espera (Hold & Resume) con Zustand.
 *
 * Reglas:
 *  • Máximo 5 tickets simultáneos en espera.
 *  • Solo viven en memoria (se pierden al recargar — son ventas sin confirmar).
 *  • holdTicket  → devuelve id del ticket guardado, o null si hay 5 ya.
 *  • resumeTicket → devuelve el HeldTicket SIN quitarlo (el caller llama removeTicket).
 *  • removeTicket → elimina (cancelar o después de cobrar).
 */

import { create } from 'zustand'
import type { CartItem } from './cartStore'

export interface HeldTicket {
  id: string
  storeId: string // tienda activa al pausar — filtra por tienda en HeldTicketsBar
  label: string // "Espera 1", "Espera 2"…
  items: CartItem[]
  clientId: string | null
  clientName: string | null
  clientBusinessName: string | null
  clientLevel: 'final' | 'pro' | 'vip'
  subtotal: number
  totalDiscount: number
  tax: number
  total: number
  heldAt: string // ISO timestamp
}

interface HeldState {
  tickets: HeldTicket[]
  maxTickets: number
  holdTicket: (data: Omit<HeldTicket, 'id' | 'label' | 'heldAt'>) => string | null
  resumeTicket: (id: string) => HeldTicket | null
  removeTicket: (id: string) => void
}

export const useHeldStore = create<HeldState>((set, get) => ({
  tickets: [],
  maxTickets: 5,

  holdTicket: data => {
    const { tickets, maxTickets } = get()
    if (tickets.length >= maxTickets) return null
    const id = crypto.randomUUID()
    const label = `Espera ${tickets.length + 1}`
    const ticket: HeldTicket = { ...data, id, label, heldAt: new Date().toISOString() }
    set({ tickets: [...tickets, ticket] })
    return id
  },

  resumeTicket: id => get().tickets.find(t => t.id === id) ?? null,

  removeTicket: id => {
    const remaining = get().tickets.filter(t => t.id !== id)
    const renumbered = remaining.map((t, i) => ({ ...t, label: `Espera ${i + 1}` }))
    set({ tickets: renumbered })
  },
}))
